import type { Fertilizer } from '../../products';
import type { CorrectionWorkspaceState } from '../store/correctionStore';
import type {
	AplicacaoForma,
	CreatePlantingAdvicePayload,
	FormaAplicacaoConfig,
	PlantingAdviceAdubacaoPayload,
	PlantingAdviceCalagemPayload,
	PlantingAdviceFormaConfigPayload,
	PlantingAdviceGessagemPayload,
	PlantingAdviceMetadataPayload,
	PlantingAdviceResultadoPayload,
	ResultTableRow,
} from '../types';

const getId = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const id = record._id ?? record.id;
		return typeof id === 'string' ? id : '';
	}
	return '';
};

const number = (value: unknown): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const formaConfig = (form: {
	profundidade: string;
	largura: string;
	comprimento: string;
}): PlantingAdviceFormaConfigPayload => ({
	p: number(form.profundidade),
	l: number(form.largura),
	c: number(form.comprimento),
});

const RESULT_KEYS: Record<string, keyof PlantingAdviceResultadoPayload> = {
	'K+': 'K',
	'Sat. K': 'satK',
	'P-M1': 'Pm1',
	'pH água': 'pHagua',
	Ca: 'Ca',
	Mg: 'Mg',
	'Sat. Ca': 'satCa',
	'Sat. Mg': 'satMg',
	'Sat. bases': 'satSB',
	S_g: 'S_g',
	Ca_g: 'Ca_g',
	satCa_g: 'satCa_g',
};

const applyRows = (
	result: Partial<PlantingAdviceResultadoPayload>,
	rows: ResultTableRow[] | undefined
) => {
	for (const row of rows ?? []) {
		const key = RESULT_KEYS[row.param];
		if (key && row.after !== '—' && row.after !== '') result[key] = number(row.after);
	}
};

const replaceMetadata = (
	current: PlantingAdviceMetadataPayload[],
	next: PlantingAdviceMetadataPayload
) => [...current.filter(({ resource }) => resource !== next.resource), next];

export const buildCorrectionAdvicePayload = (
	state: CorrectionWorkspaceState,
	fertilizers: Fertilizer[] = []
): CreatePlantingAdvicePayload => {
	const { advice } = state;
	if (!advice?._id) throw new Error('Recomendação não encontrada para salvar.');

	const findFertilizer = (id: string) => fertilizers.find(({ _id }) => _id === id);
	const result: Partial<PlantingAdviceResultadoPayload> = { ...(advice.resultado ?? {}) };
	applyRows(result, state.calagemResult?.table);
	applyRows(result, state.fosfatagemResult?.table);
	applyRows(result, state.gessagemResult?.table);

	const formasAplicacao: FormaAplicacaoConfig = { ...(advice.formasAplicacao ?? {}) };
	let metadatas = [...(advice.metadatas ?? [])] as PlantingAdviceMetadataPayload[];

	let calagem: PlantingAdviceCalagemPayload | undefined = advice.calagem
		? { ...advice.calagem, product: getId(advice.calagem.product) }
		: undefined;
	if (state.calagemHasCalculated && state.calagem.forma && state.calagem.corretivo) {
		const form = state.calagem;
		const forma = form.forma as AplicacaoForma;
		const ajuste = state.calagemAjuste;
		const product = findFertilizer(form.corretivo);
		const config = formaConfig(form);
		formasAplicacao[forma] = config;
		calagem = {
			metodo: form.metodo === 'CaMg' ? 'CaMg2' : 'satCa2',
			metodoValue: number(form.metodoValue),
			product: form.corretivo,
			dose: number(ajuste.dose),
			forma,
			formaValue: number(ajuste.doseSecundaria),
			CaO: number(product?.CaO),
			MgO: number(product?.MgO),
		};
		metadatas = replaceMetadata(metadatas, {
			resource: 'calcularCalagem',
			meta: {
				adviceId: advice._id,
				productId: form.corretivo,
				metodo: form.metodo,
				metodoValue: number(form.metodoValue),
				forma,
				formaAplicacao: config,
				dose: number(ajuste.dose),
				doseMax: number(state.calagemResult?.doseMax),
				formaValue: number(ajuste.doseSecundaria),
			},
		});
	}

	let adubacao: PlantingAdviceAdubacaoPayload | undefined = advice.adubacao
		? {
				forma: advice.adubacao.forma,
				products: advice.adubacao.products.map((product) => ({
					...product,
					productId: product.productId || getId(product.product),
				})),
			}
		: undefined;
	if (state.fosfatagemHasCalculated && state.fosfatagem.forma) {
		const form = state.fosfatagem;
		const forma = form.forma as AplicacaoForma;
		const config = formaConfig(form);
		formasAplicacao[forma] = config;
		const selectedIds = [form.fertilizanteP, form.fertilizanteComplementar].filter(Boolean);
		const responseProducts = state.fosfatagemResult?.products ?? [];
		const products = selectedIds.map((productId, index) => {
			const calculated = responseProducts.find((item) => item.productId === productId);
			const fertilizer = findFertilizer(productId);
			const primary = index === 0;
			return {
				productId,
				dose: number(
					calculated?.dose ??
						(primary ? state.fosfatagemAjuste.dose : state.fosfatagemAjuste.doseComplementar)
				),
				formaValue: number(
					calculated?.formaValue ??
						(primary
							? state.fosfatagemAjuste.doseSecundaria
							: state.fosfatagemAjuste.doseSecundariaComplementar)
				),
				custo: 0,
				N: number(calculated?.N ?? fertilizer?.N),
				P2O5: number(calculated?.P2O5 ?? fertilizer?.P2O5),
				K2O: number(calculated?.K2O ?? fertilizer?.K2O),
			};
		});
		adubacao = { forma, products };
		metadatas = replaceMetadata(metadatas, {
			resource: 'calcularAdubacao',
			meta: {
				adviceId: advice._id,
				forma,
				formaAplicacao: config,
				products,
			},
		});
	}

	let gessagem: PlantingAdviceGessagemPayload | undefined = advice.gessagem
		? {
				...advice.gessagem,
				productId: advice.gessagem.productId || getId(advice.gessagem.product),
			}
		: undefined;
	if (state.gessagemHasCalculated && state.gessagem.forma && state.gessagem.condicionador) {
		const form = state.gessagem;
		const forma = form.forma as AplicacaoForma;
		const ajuste = state.gessagemAjuste;
		const product = findFertilizer(form.condicionador);
		const config = {
			...formaConfig(form),
			p: forma === 'total' || forma === 'faixa' ? 0.2 : number(form.profundidade),
		};
		formasAplicacao[forma] = config;
		gessagem = {
			soilAnalysis: form.soilAnalysis,
			satCa2: number(form.satCa2),
			productId: form.condicionador,
			dose: number(ajuste.dose),
			forma,
			formaValue: number(ajuste.doseSecundaria),
			interpretacao: form.interpretacao,
			CaO: number(product?.CaO ?? product?.Ca),
			S: number(product?.S),
		};
		metadatas = replaceMetadata(metadatas, {
			resource: 'calcularGessagem',
			meta: {
				adviceId: advice._id,
				soilAnalysisId: form.soilAnalysis,
				productId: form.condicionador,
				satCa2: number(form.satCa2),
				forma,
				formaAplicacao: config,
				dose: number(ajuste.dose),
				doseMax: number(state.gessagemResult?.doseMax),
				formaValue: number(ajuste.doseSecundaria),
			},
		});
	}

	return {
		_id: advice._id,
		name: state.recommendation.identificacao.trim() || advice.name,
		note: state.recommendation.observacoes,
		prop: getId(advice.prop),
		area: getId(advice.area),
		sectors: advice.sectors.map(getId).filter(Boolean),
		soilAnalysis: getId(advice.soilAnalysis),
		formasAplicacao,
		...(calagem ? { calagem } : {}),
		...(adubacao ? { adubacao } : {}),
		...(gessagem ? { gessagem } : {}),
		resultado: result as PlantingAdviceResultadoPayload,
		metadatas,
		...(advice.isUsed ? { isUsed: advice.isUsed } : {}),
	};
};
